import React, { useCallback } from 'react';
import {
  Box,
  Button,
  IconButton,
  Typography,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  useTheme
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import MoreIcon from '@mui/icons-material/MoreVert';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import SellIcon from '@mui/icons-material/Sell';
import TimelineIcon from '@mui/icons-material/Timeline';
import DashboardIcon from '@mui/icons-material/Dashboard';
import ReceiptIcon from '@mui/icons-material/Receipt';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import ChartIcon from '@mui/icons-material/ShowChart';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import useTouchInteraction from '../../../hooks/useTouchInteraction';
import { shouldEnableOptionChain } from '../../../services/utilities';

// Helper function to check if instrument is an index
const isIndex = (instrument) => {
  return instrument?.index  === true;
};

const ActionItem = React.memo(({ icon, label, onClick, color, disabled }) => {
  const theme = useTheme();
  const { handlers, touchStates } = useTouchInteraction(onClick, true);

  return (
    <ListItem
      {...handlers}
      disabled={disabled}
      sx={{
        opacity: disabled ? 0.5 : 1,
        backgroundColor: touchStates.isActive && !touchStates.isTouchMoved ? 'action.hover' : 'transparent',
        transition: 'background-color 0.15s ease',
        WebkitTapHighlightColor: 'transparent',
        '&:active': {
          backgroundColor: !touchStates.isTouchMoved ? 'action.hover' : 'transparent'
        },
        py: 1.5
      }}
    >
      <ListItemIcon sx={{ 
        color: color || theme.palette.text.secondary,
        minWidth: 40
      }}>
        {icon}
      </ListItemIcon>
      <ListItemText 
        primary={label}
        primaryTypographyProps={{
          sx: {
            color: color || theme.palette.text.primary,
            fontWeight: 500
          }
        }}
      />
    </ListItem>
  );
});

const MobileActions = React.memo(({
  instrument,
  open,
  onClose,
  onBuy,
  onSell,
  onMarketDepth,
  onDelete,
  userSettings
}) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const themeMode = useSelector(state => state.theme.mode);

  const { handlers: closeHandlers, touchStates: closeTouchStates } = useTouchInteraction(onClose, true);

  const handleOptionChainClick = useCallback((e) => {
    e.stopPropagation();
    if (instrument?.exchange && instrument?.token) {
      navigate(`/overview/tdv/${instrument.exchange}/${instrument.token}`, {
        state: { defaultTab: 'chart' }
      });
      onClose();
    }
  }, [instrument?.exchange, instrument?.token, navigate, onClose]);

  const handleBuyClick = useCallback((e) => {
    e.stopPropagation();
    onBuy(instrument, e, userSettings);
    onClose();
  }, [instrument, onBuy, onClose]);

  const handleSellClick = useCallback((e) => {
    e.stopPropagation();
    onSell(instrument, e, userSettings);
    onClose();
  }, [instrument, onSell, onClose]);

  const handleMarketDepthClick = useCallback((e) => {
    e.stopPropagation();
    onMarketDepth(e);
    onClose();
  }, [onMarketDepth, onClose]);

  const handleDeleteClick = useCallback((e) => {
    e.stopPropagation();
    onDelete(e);
    onClose();
  }, [onDelete, onClose]);

  const isInstrumentIndex = isIndex(instrument);

  return (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          maxHeight: '75vh',
          borderTopLeftRadius: 16,
          borderTopRightRadius: 16,
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: themeMode === 'dark' 
            ? theme.palette.background.paper 
            : theme.palette.background.default,
          color: theme.palette.text.primary,
          overflow: 'hidden'
        }
      }}
      BackdropProps={{
        sx: {
          backgroundColor: 'rgba(0, 0, 0, 0.4)'
        },
        onClick: onClose,
        transitionDuration: 0
      }}
      transitionDuration={150}
      disableScrollLock
      slotProps={{
        backdrop: {
          invisible: false,
          sx: {
            WebkitTapHighlightColor: 'transparent'
          }
        }
      }}
    >
      <Box sx={{
        position: 'sticky',
        top: 0,
        zIndex: 1,
        backgroundColor: 'inherit',
        borderTopLeftRadius: 16,
        borderTopRightRadius: 16,
        boxShadow: theme.shadows[2],
        pb: 0.5
      }}>
        <Box
          {...closeHandlers}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 40,
            height: 40,
            borderRadius: '50%',
            backgroundColor: closeTouchStates.isActive && !closeTouchStates.isTouchMoved ? 'action.hover' : 'transparent',
            transition: 'background-color 0.15s ease',
            WebkitTapHighlightColor: 'transparent',
            '&:active': {
              backgroundColor: !closeTouchStates.isTouchMoved ? 'action.hover' : 'transparent'
            }
          }}
        >
          <CloseIcon sx={{ 
            color: theme.palette.text.secondary,
            pointerEvents: 'none'
          }} />
        </Box>

        <Typography 
          variant="subtitle1" 
          sx={{ 
            px: 2, 
            py: 1.5, 
            fontWeight: 600,
            color: theme.palette.text.primary
          }}
        >
          {instrument?.display_name}
        </Typography>
      </Box>

      <List sx={{
        pt: 0,
        pb: 2,
        overflowY: 'auto',
        overscrollBehavior: 'contain',
        WebkitOverflowScrolling: 'touch',
        backgroundColor: 'inherit',
        '&::-webkit-scrollbar': {
          display: 'none'
        },
        scrollbarWidth: 'none',
        msOverflowStyle: 'none'
      }}>
        <ActionItem
          icon={<ExpandMoreIcon />}
          label="Market Depth"
          onClick={handleMarketDepthClick}
        />

        {!isInstrumentIndex && (
          <ActionItem
            icon={<ShoppingCartIcon />}
            label="Buy"
            onClick={handleBuyClick}
            color={theme.palette.success.main}
            disabled={!instrument?.tradable}
          />
        )}

        {!isInstrumentIndex && (
          <ActionItem
            icon={<SellIcon />}
            label="Sell"
            onClick={handleSellClick}
            color={theme.palette.error.main}
            disabled={!instrument?.tradable}
          />
        )}

       
          <ActionItem
            icon={<TimelineIcon />}
            color={theme.palette.primary.main}
            label={shouldEnableOptionChain(instrument) ? "Option Chain + Charts" : "Charts"}
            onClick={handleOptionChainClick}
          />
        

        <ActionItem
          icon={<DeleteIcon />}
          label="Delete"
          onClick={handleDeleteClick}
          color={theme.palette.error.main}
        />
      </List>
    </Drawer>
  );
});

export default MobileActions; 